import React, { FC, RefObject } from 'react';
import { Typography, Skeleton } from 'antd';
import { useSnapshot } from 'valtio';
import LayoutUI from './LayoutUI';
import { formStore } from '../../store';
import { useEvaluateFormAccess } from '../../use/data/useEvaluateFormAccess';
import type { UIElement } from '#types/System.types';
import UnmappedFieldsUI from './UnmappedFieldsUI';
import type { ReadOnlyReason } from './readOnlyReason';
import { useKeyboardNavigation } from '@/keyboard';
import { usePdfExportContext } from '../pdfExport/context/PdfExportContext';

const { Text } = Typography;

/**
 * Properties for the FormUI component
 */
interface FormUIProps {
  /**
   * Reference to the content container div element for external manipulation.
   * Optional because a ref has exactly one owner (SYD-2441): the PDF export
   * portal already holds its own ref on the padded container it captures, and a
   * second claim on the same object nulls it the moment either div detaches.
   */
  contentRef?: RefObject<HTMLDivElement | null>;
  /**
   * Forces the entire form into a disabled/read-only state. The PDF export
   * portal sets it, so it disables controls but never contributes to
   * `readOnlyReason` — an export must not tell its reader about permissions.
   */
  forceDisabled?: boolean;
}


/**
 * Main form rendering component that handles different display modes and layouts.
 *
 * This component serves as the primary interface for rendering forms with support for:
 * - Standard single-section forms
 * - Show-value mode: Filtered form display based on field values
 * - Error handling for missing or invalid form layouts
 *
 * The component uses context providers to access form configuration and split data,
 * then renders appropriate FormSection components based on the current display mode.
 * It provides a flex container with consistent styling and spacing for all form content.
 *
 * @param props - The component properties
 * @param props.contentRef - Reference to the content container for external DOM manipulation
 * @returns JSX element representing the complete form UI or error message
 */
const FormUI: FC<FormUIProps> = React.memo(({ contentRef, forceDisabled = false }) => {
  const snap = useSnapshot(formStore.state);
  const formType = snap.formType;
  const formTypeLoading = formType.loading;
  const defaultUILayout = formType.data?.defaultUILayout;
  const formVersionAccess = useEvaluateFormAccess({
    formId: snap.formId,
    versionId: snap.versionId,
    enabled: true,
  });
  const { isPdfExportMode } = usePdfExportContext();

  const { onKeyDown } = useKeyboardNavigation({
    mode: 'form',
    disabled: forceDisabled,
  });

  if (formTypeLoading || !defaultUILayout) {
    return <Skeleton active />;
  }

  if (!defaultUILayout.structure) {
    return <Text type='danger'>Error: No UI layout defined for this form.</Text>;
  }

  const disabled = forceDisabled || !formVersionAccess?.canWrite;
  /**
   * The grant, not `canWrite`. A closed or approved version has `canWrite` false
   * for everyone, and telling a reader who holds `formsWrite` that they lack
   * permission would be false — the freeze is spoken for by the held-push alert,
   * which words it as waiting rather than as a denial (SYD-2455).
   */
  const readOnlyReason: ReadOnlyReason | undefined =
    !formVersionAccess.loading && !formVersionAccess.hasWriteAccess ? 'noWriteAccess' : undefined;

  return (
    <div
      ref={contentRef}
      onKeyDown={onKeyDown}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        alignSelf: 'stretch',
        gap: '2px',
        paddingBottom: isPdfExportMode ? 0 : 25,
      }}
    >
      <LayoutUI
        uiElement={defaultUILayout.structure as UIElement}
        disabled={disabled}
        readOnlyReason={readOnlyReason}
      />
      {!isPdfExportMode && (
        <UnmappedFieldsUI
          uiElement={defaultUILayout.structure as UIElement}
          disabled={disabled}
        />
      )}
    </div>
  );
});

FormUI.displayName = 'FormUI';

export default FormUI;